import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLineInView } from "./motion";

const PARENT = {
  name: "Aether Verge Holdings, Inc.",
  tag: "Parent company",
  logo: "/assets/logos/aether.jpg",
  blurb:
    "Aether Verge Holdings serves as the central corporate entity managing a diversified portfolio of publishing imprints, software assets, and intellectual property. It provides the foundational infrastructure and strategic oversight for both technical and creative ventures.",
};

const SUBSIDIARIES = [
  {
    name: "Convex Software Inc.",
    tag: "Software development",
    logo: "/assets/logos/convex.jpg",
    id: "convex",
    blurb:
      "The primary entity for software development and operational management — overseeing the engineering, deployment, and maintenance of specialized digital tools and applications.",
  },
  {
    name: "PressCraft",
    tag: "Typesetting software",
    logo: "/assets/logos/presscraft.jpg",
    id: "presscraft",
    blurb:
      "Custom-engineered typesetting software that automates complex document formatting and streamlines the book production pipeline, handling precise manuscript layout requirements.",
  },
  {
    name: "Lion Star Publishing",
    tag: "Publishing imprint",
    logo: "/assets/logos/lionstar.jpg",
    id: "lionstar",
    blurb:
      "A dedicated imprint managing a broad catalog of independent publications — non-fiction psychology, satirical comedy fiction, and technical reference guides including The Aether Verge Collective.",
  },
  {
    name: "Project Lexicon",
    tag: "AI manuscript analysis",
    logo: "/assets/logos/lexicon.jpg",
    id: "lexicon",
    blurb:
      "An AI-powered live web platform that evaluates manuscript structure and literary content with psychometric algorithms, delivering deep structural analysis to refine written works.",
  },
];

function VentureCard({ v, index }) {
  return (
    <FadeUp
      delay={0.1 + index * 0.08}
      data-testid={`venture-card-${v.id}`}
      className="group flex flex-col gap-6 border border-line bg-ink2 p-6 transition-colors duration-500 hover:border-vermilion/40 sm:p-7"
    >
      <div className="flex items-start gap-5">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden border border-line bg-[#2a2c30]">
          <img
            src={v.logo}
            alt={`${v.name} logo`}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
            {v.tag}
          </p>
          <h3 className="font-serif text-2xl font-normal leading-tight text-bone">
            {v.name}
          </h3>
        </div>
        <ArrowUpRight
          className="h-4 w-4 shrink-0 text-smoke transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-vermilion"
          strokeWidth={1.5}
        />
      </div>
      <p
        data-testid={`venture-blurb-${v.id}`}
        className="text-sm leading-relaxed text-ash"
      >
        {v.blurb}
      </p>
    </FadeUp>
  );
}

export default function Ventures() {
  return (
    <section
      id="ventures"
      data-testid="ventures-section"
      className="relative border-t border-line py-20 sm:py-28"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <FadeUp className="mb-16 flex items-end justify-between gap-6">
          <div>
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion">
              02 — The Ventures
            </p>
            <h2 className="font-serif text-4xl font-light leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              <MaskLineInView>One holding.</MaskLineInView>
              <MaskLineInView delay={0.08}>
                Four houses<span className="text-vermilion">.</span>
              </MaskLineInView>
            </h2>
          </div>
          <p className="hidden max-w-xs text-right font-mono text-[11px] uppercase leading-relaxed tracking-[0.2em] text-smoke md:block">
            Publishing · Software ·
            <br />
            Typesetting · AI
          </p>
        </FadeUp>

        <div className="grid gap-6 lg:grid-cols-12">
          <FadeUp
            data-testid="venture-card-aether"
            className="group relative flex flex-col justify-between overflow-hidden border border-line bg-ink lg:col-span-5"
          >
            <div className="relative aspect-[7/6] overflow-hidden border-b border-line bg-[#2a2c30]">
              <img
                src={PARENT.logo}
                alt="Aether Verge Holdings logo"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="pointer-events-none absolute inset-0 spotlight opacity-70" />
              <span className="absolute left-0 top-0 h-6 w-6 border-l-2 border-t-2 border-vermilion" />
              <span className="absolute bottom-0 right-0 h-6 w-6 border-b-2 border-r-2 border-vermilion" />
            </div>
            <div className="p-8 sm:p-10">
              <p className="mb-4 inline-flex border border-vermilion/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
                {PARENT.tag}
              </p>
              <h3 className="font-serif text-3xl font-light leading-tight text-bone sm:text-4xl">
                {PARENT.name}
              </h3>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ash">
                {PARENT.blurb}
              </p>
            </div>
          </FadeUp>

          <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-1 xl:grid-cols-2">
            {SUBSIDIARIES.map((v, i) => (
              <VentureCard key={v.id} v={v} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
